/**
 * Reads the layout of a ZPL label template, so an on-screen preview can draw
 * exactly what the Zebra prints (label print fidelity plan, 2026-07-31).
 *
 * Supports the commands the shipped templates use: ^PW/^LL (label size in
 * dots), ^FO (field origin), ^GB (box), ^A0N (font height/width), ^FB (text
 * block: width, max lines, extra line spacing), ^FR (reverse, white on black),
 * ^BQN (QR code, magnification) and ^FD{{placeholder}}. Anything else is
 * ignored, so a company override using other commands still previews its
 * known parts.
 */

export interface ZplBox {
  kind: "box";
  x: number;
  y: number;
  width: number;
  height: number;
  /** Border thickness; equal to the height (or width) means a filled box. */
  thickness: number;
}

export interface ZplText {
  kind: "text";
  x: number;
  y: number;
  placeholder: string;
  fontHeight: number;
  fontWidth: number;
  reverse: boolean;
  /** Present when the field is a ^FB text block. */
  block?: { width: number; maxLines: number; lineSpacing: number };
}

export interface ZplQr {
  kind: "qr";
  x: number;
  y: number;
  placeholder: string;
  magnification: number;
}

export type ZplElement = ZplBox | ZplText | ZplQr;

export interface ZplLayout {
  widthDots: number;
  heightDots: number;
  elements: ZplElement[];
}

const num = (s: string | undefined, fallback: number) => {
  const n = parseInt(s ?? "", 10);
  return Number.isNaN(n) ? fallback : n;
};

export function parseZplLayout(zpl: string, fallbackSize: { widthDots: number; heightDots: number }): ZplLayout {
  const layout: ZplLayout = { ...fallbackSize, elements: [] };
  const pw = zpl.match(/\^PW(\d+)/);
  const ll = zpl.match(/\^LL(\d+)/);
  if (pw) layout.widthDots = num(pw[1], layout.widthDots);
  if (ll) layout.heightDots = num(ll[1], layout.heightDots);

  // Each field runs from ^FO to ^FS.
  const fieldRe = /\^FO(\d+),(\d+)([\s\S]*?)\^FS/g;
  let m: RegExpExecArray | null;
  while ((m = fieldRe.exec(zpl))) {
    const x = num(m[1], 0);
    const y = num(m[2], 0);
    const body = m[3];

    const gb = body.match(/\^GB(\d+),(\d+),(\d+)/);
    if (gb) {
      layout.elements.push({ kind: "box", x, y, width: num(gb[1], 0), height: num(gb[2], 0), thickness: num(gb[3], 1) });
      continue;
    }

    const bq = body.match(/\^BQN?,\d+,(\d+)/);
    const fd = body.match(/\^FD(?:[HQML][AM],)?\{\{(\w+)\}\}/);
    if (bq && fd) {
      layout.elements.push({ kind: "qr", x, y, placeholder: fd[1], magnification: num(bq[1], 2) });
      continue;
    }

    const font = body.match(/\^A0N,(\d+)(?:,(\d+))?/);
    if (fd) {
      const fontHeight = num(font?.[1], 30);
      const fb = body.match(/\^FB(\d+),(\d+),(\d+)/);
      layout.elements.push({
        kind: "text",
        x,
        y,
        placeholder: fd[1],
        fontHeight,
        fontWidth: num(font?.[2], fontHeight),
        reverse: /\^FR/.test(body),
        block: fb ? { width: num(fb[1], 0), maxLines: Math.max(1, num(fb[2], 1)), lineSpacing: num(fb[3], 0) } : undefined,
      });
    }
  }
  return layout;
}
